/**
 * Client-side targeting decisions, pure (2026-10-16).
 *
 * Reported as "targeting for spells is not working properly". The client
 * half of that was three separate mistakes in the bootstraps, each a few
 * lines of inline code nobody could test:
 *
 * - **Tab skipped the nearest monster.** The dungeon kept a running index,
 *   incremented it *before* reading, and re-sorted by distance every press — so
 *   the first press picked the second nearest, and later presses landed on
 *   whatever happened to be at that position in a list that had moved.
 *   `pickCycleTarget` finds the current target in the fresh list and steps
 *   past it, which is what the arena already did.
 * - **Every ability auto-picked the nearest monster** — heals included — with
 *   no idea of range or facing, and happily picked a skeleton still hidden in
 *   the dark. `pickAbilityTarget` only acts for ENEMY abilities and prefers
 *   what the player can see, can reach and is facing.
 * - **Unrevealed monsters were targetable.** Monsters are streamed at 16m but
 *   only *seen* within 4m (dark) / 10m (lit) — `render/monster-reveal.ts`.
 *   Every candidate here carries `revealed`, and nothing unrevealed is chosen.
 */

export interface TargetCandidate {
  id: string;
  x: number;
  z: number;
  alive: boolean;
  /** Whether the player can actually see it (monster-reveal); always true for what is not hidden by darkness. */
  revealed: boolean;
  /**
   * Added to the distance when ranking — the arena's "people before
   * skeletons" bias. 0 when absent.
   */
  biasM?: number;
}

export interface Viewer {
  x: number;
  z: number;
  /** Radians, the server's convention: forward is (sin f, cos f). */
  facing?: number;
}

/** Live, visible candidates nearest first (with each one's bias). */
function ranked(candidates: readonly TargetCandidate[], me: Viewer): TargetCandidate[] {
  return candidates
    .filter((c) => c.alive && c.revealed)
    .map((c) => ({ c, d: Math.hypot(c.x - me.x, c.z - me.z) + (c.biasM ?? 0) }))
    .sort((a, b) => a.d - b.d || (a.c.id < b.c.id ? -1 : 1))
    .map(({ c }) => c);
}

/**
 * Tab: the next target after the current one, nearest first. With no current
 * target (or one that is no longer a candidate) it is the nearest — the bug
 * was the first press landing on the second nearest. '' when there is
 * nothing to pick.
 */
export function pickCycleTarget(candidates: readonly TargetCandidate[], me: Viewer, currentId: string): string {
  const list = ranked(candidates, me);
  if (list.length === 0) return '';
  const index = list.findIndex((c) => c.id === currentId);
  return list[(index + 1) % list.length].id;
}

export type AbilityTargeting = 'SELF' | 'ALLY' | 'ENEMY' | 'AOE_ENEMY';

/**
 * Whether `c` is in front of the viewer — a generous 180-degree arc, the same
 * one the arena server uses for its untargeted pick.
 */
function inFront(c: { x: number; z: number }, me: Viewer): boolean {
  if (me.facing === undefined) return true;
  const dx = c.x - me.x;
  const dz = c.z - me.z;
  const d = Math.hypot(dx, dz);
  if (d < 0.01) return true;
  return (dx / d) * Math.sin(me.facing) + (dz / d) * Math.cos(me.facing) > 0;
}

/**
 * The subject an ability press should go out with.
 *
 * Returns the id to target, or null to leave the current target alone (the
 * caller then sends nothing). Only ENEMY abilities pick: SELF and AOE need no
 * subject, and ALLY abilities are resolved by the server (most wounded
 * squadmate in reach), so stealing the player's selection for them would only
 * lose it.
 *
 * - A live, visible current target the player chose stays, wherever it is —
 *   the server will say "Too far away", which is the honest answer.
 * - A live, visible *implicit* target stays while it is in range.
 * - Otherwise the best visible candidate: in range beats out of range, in
 *   front beats behind, nearer beats further.
 */
export function pickAbilityTarget(args: {
  targeting: AbilityTargeting;
  rangeM?: number;
  candidates: readonly TargetCandidate[];
  me: Viewer;
  currentId: string;
  currentIsExplicit: boolean;
}): string | null {
  const { targeting, candidates, me, currentId } = args;
  if (targeting !== 'ENEMY') return null;
  const range = args.rangeM ?? Infinity;
  const dist = (c: { x: number; z: number }): number => Math.hypot(c.x - me.x, c.z - me.z);

  const current = currentId ? candidates.find((c) => c.id === currentId) : undefined;
  if (current && current.alive && current.revealed && (args.currentIsExplicit || dist(current) <= range)) return null;

  let best: TargetCandidate | null = null;
  let bestScore = Infinity;
  for (const c of ranked(candidates, me)) {
    const d = dist(c) + (c.biasM ?? 0);
    // Tiered: out of range costs more than any distance, behind costs more
    // than any in-front distance within the same tier.
    const score = (dist(c) <= range ? 0 : 2_000) + (inFront(c, me) ? 0 : 1_000) + d;
    if (score < bestScore) { bestScore = score; best = c; }
  }
  if (!best) return null;
  return best.id === currentId ? null : best.id;
}

/**
 * Implicit targeting from a `combat.result` (QA critic defect C, 2026-10-16).
 *
 * The frame used to jump to *any* monster that hit you, mid-fight, every
 * time a second one joined in. Now only a blow YOU landed on a hostile can
 * pick the target, and only when there is no live target already — explicit
 * or implicit. A dead or absent target frees the slot; anything alive keeps it.
 *
 * Returns the id to target, or null to leave the current one alone.
 */
export function pickImplicitTarget(args: {
  attackerId: string;
  defenderId: string;
  selfId: string;
  currentId: string;
  /** Whether the current target is still there and alive (false when there is none). */
  currentLive: boolean;
  /** Whether an id is something the player may target as an enemy. */
  isHostile: (id: string) => boolean;
}): string | null {
  const { attackerId, defenderId, selfId, currentId } = args;
  if (!selfId || attackerId !== selfId) return null;
  if (!defenderId || defenderId === selfId || !args.isHostile(defenderId)) return null;
  if (currentId && args.currentLive) return null;
  return defenderId === currentId ? null : defenderId;
}

/**
 * Party-frame order to a session id, for the F1–F4 keys: F1 is yourself, F2
 * the first squadmate in the frames, and so on. '' when the slot is empty.
 */
export function partyKeyTarget(index: number, selfId: string, partyOrder: readonly string[]): string {
  if (index === 0) return selfId;
  return partyOrder[index - 1] ?? '';
}
